import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { MaskLineInView } from "./MaskLineInView";
import { SectionLabel } from "./Reveal";
import { CONTACT } from "../data/content";
import { useLang } from "../lib/lang";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full border-b border-white/20 bg-transparent py-4 text-lg text-white placeholder:text-zinc-500 outline-none transition-colors focus:border-[var(--acid)]";

export const Contact = () => {
  const { tr, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "", project_type: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, lang });
      toast.success(tr({ es: "¡Mensaje enviado! Te respondo pronto.", en: "Message sent! I'll get back to you soon." }));
      setForm({ name: "", email: "", message: "", project_type: "" });
    } catch {
      toast.error(tr({ es: "No se pudo enviar. Escríbeme por email.", en: "Couldn't send. Please email me instead." }));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-36">
      <SectionLabel index="09">{tr({ es: "Contacto", en: "Contact" })}</SectionLabel>
      <h2 className="mt-8 font-display text-[10.5vw] font-extrabold uppercase leading-[0.9] tracking-[-0.03em] lg:text-[7.5rem]">
        <MaskLineInView>{tr(CONTACT.title1)}</MaskLineInView>
        <MaskLineInView delay={0.12}><span className="font-serif-i normal-case text-[var(--acid)]">{tr(CONTACT.title2)}</span></MaskLineInView>
      </h2>
      <div className="mt-16 grid gap-16 lg:grid-cols-12">
        <div className="space-y-8 lg:col-span-4">
          <a data-testid="contact-email-link" href={`mailto:${CONTACT.email}`} className="group block">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Email</span>
            <span className="mt-2 flex items-center gap-2 break-all text-xl font-semibold group-hover:text-[var(--acid)]">{CONTACT.email}<ArrowUpRight size={18} /></span>
          </a>
          <a data-testid="contact-phone-link" href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="group block">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{tr({ es: "Teléfono", en: "Phone" })}</span>
            <span className="mt-2 block text-xl font-semibold group-hover:text-[var(--acid)]">{CONTACT.phone}</span>
          </a>
        </div>
        <form data-testid="contact-form" onSubmit={submit} className="lg:col-span-8">
          <div className="mb-6 flex flex-wrap gap-2">
            {CONTACT.types.map((t, i) => {
              const v = tr(t);
              const on = form.project_type === v;
              return (
                <button type="button" key={v} data-testid={`contact-type-${i}`} onClick={() => setForm((f) => ({ ...f, project_type: on ? "" : v }))}
                  className={`rounded-full border px-5 py-2 text-sm transition-colors ${on ? "border-[var(--acid)] bg-[var(--acid)] text-[var(--ink)]" : "border-white/20 text-zinc-300 hover:border-white"}`}>
                  {v}
                </button>
              );
            })}
          </div>
          <div className="grid gap-2 sm:grid-cols-2 sm:gap-8">
            <input data-testid="contact-form-name-input" required value={form.name} onChange={set("name")} placeholder={tr({ es: "Tu nombre", en: "Your name" })} className={field} />
            <input data-testid="contact-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email" className={field} />
          </div>
          <textarea data-testid="contact-form-message-input" required rows={4} value={form.message} onChange={set("message")} placeholder={tr({ es: "Cuéntame sobre tu proyecto", en: "Tell me about your project" })} className={`${field} mt-2 resize-none`} />
          <button data-testid="contact-form-submit-btn" disabled={sending} className="group mt-10 flex items-center gap-4 rounded-full bg-[var(--acid)] py-3 pl-8 pr-3 font-semibold text-[var(--ink)] transition-transform hover:scale-[1.03] disabled:opacity-60">
            {tr({ es: "Enviar mensaje", en: "Send message" })}
            <span className="grid h-11 w-11 place-items-center rounded-full bg-[var(--ink)] text-[var(--acid)] transition-transform group-hover:rotate-45">
              {sending ? <Loader2 size={18} className="animate-spin" /> : <ArrowUpRight size={18} />}
            </span>
          </button>
        </form>
      </div>
    </section>
  );
};
